import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Plus,
  RefreshCw,
  CheckCircle,
  XCircle,
  Calendar,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  StatsCard,
  FilterSection,
  OrganizerTable,
  DeleteConfirmationModal,
  StatusConfirmationModal,
  ViewOrganizerModal,
} from "./components";
import organizerService from "../../../../api/OrganizerService";
import Loader from "../../../../components/misc/Loader";

/**
 * Main Organizer Management Component
 */
const OrganizerManagement = () => {
  const navigate = useNavigate();

  // State management
  const [organizers, setOrganizers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [copiedId, setCopiedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [organizerToDelete, setOrganizerToDelete] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [organizerToUpdateStatus, setOrganizerToUpdateStatus] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrganizers = async () => {
      setLoading(true);
      try {
        const response = await organizerService.getOrganizers();
        if (response?.status && response?.data?.status_code === 200) {
          // API response structure: response.data.data.data is the array
          const organizersData =
            response.data?.data?.data || response.data?.data || [];
          setOrganizers(organizersData);
        } else {
          toast.error(
            response?.data?.reason ||
              response?.message ||
              "Sorry, the organizers data could not be fetched"
          );
          setOrganizers([]);
        }
      } catch (error) {
        console.log("ERROR: ", error.message);
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching organizers"
        );
        setOrganizers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizers();
  }, []);

  // Copy to clipboard functionality
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(text);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Filter and search logic
  const filteredOrganizers = useMemo(() => {
    return organizers
      .filter((org) => {
        const matchesSearch =
          org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.phone_number?.includes(searchTerm);

        const matchesStatus =
          statusFilter === "all" || org.status === statusFilter;
        const matchesCountry =
          countryFilter === "all" || org.country === countryFilter;

        return matchesSearch && matchesStatus && matchesCountry;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = a.name?.toLowerCase() || "";
            bValue = b.name?.toLowerCase() || "";
            break;
          case "email":
            aValue = a.email?.toLowerCase() || "";
            bValue = b.email?.toLowerCase() || "";
            break;
          case "country":
            aValue = a.country?.toLowerCase() || "";
            bValue = b.country?.toLowerCase() || "";
            break;
          case "status":
            aValue = a.status || "";
            bValue = b.status || "";
            break;
          case "date":
            aValue = new Date(a.created_at || 0);
            bValue = new Date(b.created_at || 0);
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
  }, [organizers, searchTerm, statusFilter, countryFilter, sortBy, sortOrder]);

  // Pagination
  const paginatedOrganizers = filteredOrganizers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats calculation
  const stats = useMemo(() => {
    return {
      total: organizers.length,
      active: organizers.filter((o) => o.status === "active").length,
      inactive: organizers.filter((o) => o.status === "inactive").length,
      totalEvents: organizers.reduce((sum, o) => sum + (o.eventsCount || 0), 0),
    };
  }, [organizers]);

  // Get unique countries from organizers
  const availableCountries = useMemo(() => {
    const countries = [...new Set(organizers.map((o) => o.country))];
    return countries.sort();
  }, [organizers]);

  // Handlers
  const handleRefresh = () => {
    // TODO: Fetch from API
    toast.success("Data refreshed successfully");
  };

  const handleViewDetails = (organizer) => {
    setSelectedOrganizer(organizer);
    setShowViewModal(true);
  };

  const handleEdit = (organizer) => {
    navigate(`/admin/media/organizer/edit/${organizer.slug}`, {
      state: { organizer },
    });
  };

  const handleDeleteClick = (organizer) => {
    setOrganizerToDelete(organizer);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    // TODO: API call to delete
    const response = await organizerService.deleteOrganizer(
      organizerToDelete.slug
    );

    if (response.data.status_code !== 200) {
      toast.error(
        response?.data?.reason || "An error occurred while deleting organizer"
      );
      return;
    }
    toast.success(response?.data?.reason || "Organizer deleted successfully");
    setOrganizers((prev) =>
      prev.filter((org) => org.slug !== organizerToDelete.slug)
    );
    toast.success(`${organizerToDelete.name} deleted successfully`);
    setShowDeleteModal(false);
    setOrganizerToDelete(null);
  };

  const handleStatusConfirm = () => {
    if (!organizerToUpdateStatus) return;

    const newStatus =
      organizerToUpdateStatus.status === "active" ? "inactive" : "active";

    // TODO: API call to update status
    // await organizerService.updateStatus(organizerToUpdateStatus.slug, newStatus);

    setOrganizers((prev) =>
      prev.map((org) =>
        org.slug === organizerToUpdateStatus.slug
          ? { ...org, status: newStatus }
          : org
      )
    );

    toast.success(
      `${organizerToUpdateStatus.name} has been ${
        newStatus === "active" ? "activated" : "deactivated"
      } successfully`
    );

    setShowStatusModal(false);
    setOrganizerToUpdateStatus(null);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCountryFilter("all");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="px-3 py-4 space-y-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 sm:space-y-6 lg:space-y-8">
        {/* Header Section */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Building2 className="w-6 h-6 text-white sm:w-7 sm:h-7 lg:w-8 lg:h-8" />
              </div>
              <div>
                <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
                  Organizer Management
                </h1>
                <p className="mt-1 text-sm text-gray-600 sm:text-base">
                  Manage event and tour organizers
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleRefresh}
              className="bg-white text-gray-700 px-4 py-2.5 sm:px-4 sm:py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all flex items-center justify-center min-h-[44px] w-full sm:w-auto border border-gray-200 shadow-sm hover:shadow-md font-medium"
            >
              <RefreshCw className="flex-shrink-0 mr-2 w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => navigate("/admin/media/organizer/create")}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl transition-all flex items-center justify-center shadow-md hover:shadow-lg min-h-[44px] w-full sm:w-auto font-semibold"
            >
              <Plus className="flex-shrink-0 mr-2 w-4 h-4" />
              Add New Organizer
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4 lg:gap-6">
          <StatsCard
            title="Total Organizers"
            value={stats.total}
            icon={Building2}
            iconBgColor="bg-blue-50"
            iconColor="text-blue-600"
            valueColor="text-gray-900"
          />
          <StatsCard
            title="Active"
            value={stats.active}
            icon={CheckCircle}
            iconBgColor="bg-emerald-50"
            iconColor="text-emerald-600"
            valueColor="text-emerald-600"
          />
          <StatsCard
            title="Inactive"
            value={stats.inactive}
            icon={XCircle}
            iconBgColor="bg-gray-100"
            iconColor="text-gray-500"
            valueColor="text-gray-500"
          />
          <StatsCard
            title="Total Events"
            value={stats.totalEvents}
            icon={Calendar}
            iconBgColor="bg-purple-50"
            iconColor="text-purple-600"
            valueColor="text-purple-600"
          />
        </div>

        {/* Filters Section */}
        <FilterSection
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          countryFilter={countryFilter}
          onCountryChange={setCountryFilter}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          onClearFilters={handleClearFilters}
          countries={availableCountries}
        />

        {/* Table Section */}
        <OrganizerTable
          organizers={paginatedOrganizers}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          copiedId={copiedId}
          onCopy={copyToClipboard}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalCount={filteredOrganizers.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Organizer"
        itemName={organizerToDelete?.name}
        warningMessage={`This operation is permanent and cannot be reversed.`}
        confirmButtonText="Delete Organizer"
      />

      {/* Status Confirmation Modal */}
      <StatusConfirmationModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setOrganizerToUpdateStatus(null);
        }}
        onConfirm={handleStatusConfirm}
        organizer={organizerToUpdateStatus}
      />

      {/* View Organizer Modal */}
      <ViewOrganizerModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        organizer={selectedOrganizer}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default OrganizerManagement;
